// Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Button,
  CurrencyInput
} from '../'

import {
  showAsCurrency,
  encodeBase64,
  decodeBase64
} from '../../utils'

import { CONTENTTYPE_CONSOLIDATION } from '../../constants'

const QUERY_FILERELATIONS = gql`
  query($contentType: ID!, $consolidation: UUID!) {
    filerelations(contentType_Id: $contentType, objectId: $consolidation, orderBy: "-created_at", first: 1) {
      edges {
        node {
          id
          file {
            id
            asset
          }
        }
      }
    }
  }
`

const MUTATION_FILERELATION = gql`
  mutation createFileRelation($input: FileRelationMutationInput!) {
    filerelationMutate(input: $input) {
      filerelation {
        id
        file {
          id
          asset
        }
      }
    }
  }
`


export class ConsolidationEdit extends React.PureComponent {
  static propTypes = {
    consolidation: PropTypes.string.isRequired,
    accountBalance: PropTypes.number.isRequired,
    amount: PropTypes.number,
    statementExpiresAt: PropTypes.string,
    selectedStatementFile: PropTypes.string,
    linkedConsolidationFiles: PropTypes.array.isRequired,
    updateLinkedConsolidationFiles: PropTypes.func.isRequired
  }

  state = {
    amount: this.props.amount,
    statementExpiresAt: this.props.statementExpiresAt ? moment(this.props.statementExpiresAt) : null
  }

  valuesChanged = () => (
    this.state.amount !== this.props.amount ||  // NOTE: double check string vs int values for 'amount'
    this.hasStatementExpiredAtChanged()
  )

  hasStatementExpiredAtChanged = () => (
    this.state.statementExpiresAt && !this.state.statementExpiresAt.isSame(moment(this.props.statementExpiresAt))
  )

  updateConsolidation = (consolidationMutate) => {
    let input = {
      id: this.props.consolidation
    }
    if (this.state.amount !== this.props.amount) {
      input.amount = this.state.amount
    }
    if (this.hasStatementExpiredAtChanged()) {
      input.statementExpiresAt = moment(this.state.statementExpiresAt).format('YYYY-MM-DD')
    }
    consolidationMutate({ variables: { input } })
    .then(() => this.props.onSaved())
    .catch(error => this.props.onSaved())
  }

  linkStatementFile = (filerelationMutate) => {
    let input = {
      file: this.props.selectedStatementFile,
      contentType: CONTENTTYPE_CONSOLIDATION,
      objectId: decodeBase64(this.props.consolidation)
    }
    filerelationMutate({ variables: { input } })
      .then(({ data }) => {
        let linkedConsolidationFiles = Object.assign({}, this.props.linkedConsolidationFiles)
        linkedConsolidationFiles[this.props.consolidation] = data.filerelationMutate.filerelation.file.id
        this.props.updateLinkedConsolidationFiles(linkedConsolidationFiles)
      })
  }

  render() {
    return (
      <tr>
        <td className='consolidation-edit' colSpan={4}>
          <div className='slds-m-left_large'>
            <div className='slds-grid'>
              <div className='slds-size_1-of-3'>
                Account balance:
              </div>
              <div className='slds-size_1-of-3 slds-text-color_weak'>
                {showAsCurrency('R', this.props.accountBalance)}
              </div>
            </div>
            <div className='slds-grid slds-m-bottom_large'>
              <div className='slds-size_1-of-3'>
                Amount to consolidate:
              </div>
              <CurrencyInput
                value={this.state.amount}
                onChange={e => this.setState({ amount: e.target.value })}
              />
            </div>
            <div className='slds-grid'>
              <div className='slds-size_1-of-3'>
                Statement file:
              </div>
              <div className='slds-size_1-of-3'>
                <Mutation mutation={MUTATION_FILERELATION}>
                  {(filerelationMutate, { data: frData }) => (
                    <Query
                      query={QUERY_FILERELATIONS}
                      variables={{
                        contentType: encodeBase64(CONTENTTYPE_CONSOLIDATION, "ContentTypeNode"),
                        consolidation: decodeBase64(this.props.consolidation)
                      }}>
                      {({ loading, error, data }) => {
                        if (loading || error) {
                          return null
                        }
                        if (data.filerelations.edges.length > 0 || (frData && frData.filerelationMutate.filerelation)) {
                          return 'Linked'
                        } else {
                          return (
                            <Button
                              disabled={!this.props.selectedStatementFile}
                              onClick={() => this.linkStatementFile(filerelationMutate)}>
                              Link selected
                            </Button>
                          )
                        }
                      }}
                    </Query>
                  )}
                </Mutation>
              </div>
            </div>
            <div className='slds-grid slds-m-bottom_large'>
              <div className='slds-size_1-of-3'>
                Statement expiry:
              </div>
              <DatePicker
                selected={this.state.statementExpiresAt}
                onChange={statementExpiresAt => this.setState({ statementExpiresAt })}
                dateFormat='D MMM YYYY'
                minDate={moment()}
                maxDate={moment().add(90, 'days')}
              />
            </div>
          {/* NOTE: temporarily disable bank account input (to be reinstated with PayAccSys intregration)
            <div>
              <div className='slds-size_1-of-3 u-font-weight-bold'>
                Bank details:
              </div>
              <ConsolidationBankAccount
                consolidationId={this.props.consolidation}
                bankAccountRef={this.props.bankAccountRef}
                tableRowView={true}
              />
            </div>
            <div className='slds-m-top_large slds-align_absolute-center'>
              <Button
                type='brand'
                minWidth={110}
                onClick={this.props.onCancelClick}>
                Cancel
              </Button>
              <Mutation mutation={MUTATION_CONSOLIDATION}>
                {(consolidationMutate, { data }) => (
                  <Button
                    type='brand'
                    minWidth={110}
                    disabled={!this.valuesChanged()}
                    onClick={() => this.updateConsolidation(consolidationMutate)}>
                    Save
                  </Button>
                )}
              </Mutation>
            </div
          */}
          </div>
        </td>
      </tr>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/credit-application-credit-report/ConsolidationEdit.js